/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ------------------------------------------------------------------------------------------------------------
 * PDP IMAGE ZOOM — escape the sticky gallery's stacking context while open
 * ------------------------------------------------------------------------------------------------------------
 * The zoom modal (.pswp) renders inside .product__media, which is position: sticky on desktop — that
 * traps the modal's z-index inside its own stacking context, so it can't cover the header/info column.
 * Re-parenting to <body> broke Focal's open/close state, so instead: watch for pswp--open and toggle a
 * high z-index on .product__media only while it's present. Reverts the instant the modal closes.
 */
(function () {
  function syncZoomOpenState(pswp) {
    var media = pswp.closest('.product__media');
    if (!media) return;
    media.classList.toggle('pdp-gallery--zoom-open', pswp.classList.contains('pswp--open'));
  }

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'attributes' && m.target.classList && m.target.classList.contains('pswp')) {
        syncZoomOpenState(m.target);
      }
      for (var j = 0; j < m.addedNodes.length; j++) {
        var node = m.addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (node.classList && node.classList.contains('pswp')) syncZoomOpenState(node);
        var nested = node.querySelector && node.querySelector('.pswp');
        if (nested) syncZoomOpenState(nested);
      }
    }
  });

  function init() {
    document.querySelectorAll('.pswp').forEach(syncZoomOpenState);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();

/* ------------------------------------------------------------------------------------------------------------
 * PDP GALLERY — keep the Flickity carousel's width in sync with the flex layout
 * ------------------------------------------------------------------------------------------------------------
 * Focal's main-image carousel (<flickity-carousel>, product-media.liquid) is configured with
 * "percentPosition": false, so Flickity measures its container in pixels once at init and only re-measures
 * on a native window `resize` event. Our gallery column is flex-sized (flex: 1 1 0) so its rendered width can
 * settle to a different value slightly AFTER that initial measurement — e.g. web fonts swapping, or a
 * Recharge/Yotpo widget loading in the info column and shifting the flex distribution. Flickity never hears
 * about that internal reflow (no window resize fired), so the carousel's slide width goes stale while the
 * thumbnails below (plain CSS, always reflect the live width) don't — producing a gap between them that only
 * shows up sometimes, depending on load timing.
 *
 * Fix: watch the gallery column itself with a ResizeObserver and ask Flickity to recompute whenever its width
 * actually changes, for any reason — the same pattern used for the sticky buy box above.
 */
(function () {
  function watch(media) {
    if (media.dataset.flickitySyncBound) return;
    var carousel = media.querySelector('.product__media-list');
    if (!carousel) return;
    media.dataset.flickitySyncBound = 'true';

    var lastWidth = media.getBoundingClientRect().width;
    var ro = new ResizeObserver(function (entries) {
      var w = entries[0].contentRect.width;
      if (Math.abs(w - lastWidth) < 1) return;
      lastWidth = w;
      var instance = carousel.flickityInstance;
      if (instance && instance.then) {
        instance.then(function (f) { if (f && f.resize) f.resize(); });
      }
    });
    ro.observe(media);
  }

  function init() {
    document.querySelectorAll('.product__media').forEach(watch);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();

/* Product accordion: single-open mode. Opt in via [data-single-open] on .product-form__accordion.
 * Closing sibling tabs just reuses the theme's own open/close events, so buttons stay in sync. */
document.addEventListener('openable-element:open', function (event) {
  var accordion = event.target.closest && event.target.closest('.product-form__accordion[data-single-open]');
  if (!accordion) return;

  accordion.querySelectorAll('collapsible-content[open]').forEach(function (content) {
    if (content !== event.target) content.open = false;
  });
});

/* ------------------------------------------------------------------------------------------------------------
 * REVEAL-ON-SCROLL HELPER (custom sections)
 * ------------------------------------------------------------------------------------------------------------
 * Shared fade/rise-in for our hand-rolled custom sections. Wrap a section's content in <reveal-group> and add
 * class="reveal-item" to each element that should animate in as it scrolls into view. This replaces the
 * per-section IntersectionObserver scripts that used to be copy-pasted across sections.
 *
 * Editor-safe: connectedCallback re-inits when a section is re-rendered in Customize; disconnectedCallback
 * tears the observer down. The paired CSS lives at the bottom of theme.css (feature: reveal-on-scroll helper)
 * and keeps content visible when JS is off or reduced-motion is requested, so there is no invisibility trap.
 *
 * Note: sections built on a native Focal custom element (e.g. custom__testimonials on <testimonial-list>) use
 * Focal's own reveal-on-scroll and should NOT use this helper.
 */
class RevealGroup extends HTMLElement {
  connectedCallback() {
    const items = this.querySelectorAll('.reveal-item:not(.is-revealed)');
    if (!items.length) return;

    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    this._observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    items.forEach((el) => this._observer.observe(el));
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }
}

if (!customElements.get('reveal-group')) {
  customElements.define('reveal-group', RevealGroup);
}
